import { BarChartOutlined, ProjectOutlined, SettingOutlined, TeamOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { runUnsavedGuards } from '../hooks/useUnsavedChangesGuard';
import { useGuildId } from '../hooks/useGuildId';
import { routes } from '../lib/routes';
import { useServerStore } from '../stores/useServerStore';
import { useAuthStore } from '../stores/useAuthStore';
import Sidebar from './Sidebar';
import styles from './ServerSidebar.module.css';

/** Contextual navigation and channels for the currently selected Project. */
export default function ServerSidebar({ onTaskClick, activeThreadId, activeChannelId }: { onTaskClick?: (threadId: string) => void; activeThreadId?: string; activeChannelId?: string | null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const guildId = useGuildId();
  const activeGuild = useServerStore((state) => state.guilds.find((guild) => guild.id === guildId));
  const currentUser = useAuthStore((state) => state.currentUser);
  const select = (target: string) => {
    if (runUnsavedGuards()) navigate(target);
  };
  const path = location.pathname;

  return (
    <aside className={`current-server-rail ${styles.rail}`} aria-label="Current Project">
      <header className="current-server-header">
        <span className="current-server-name">{activeGuild?.name ?? 'No Project selected'}</span>
      </header>
      <nav className="current-server-nav" aria-label="Project resources">
        <button
          type="button"
          className={path.endsWith('/insights') ? 'current-server-nav-active' : ''}
          disabled={!guildId}
          onClick={() => guildId && select(routes.insights(guildId))}
        >
          <BarChartOutlined /> Project Insights
        </button>
        <button
          type="button"
          className={path.endsWith('/tasks') ? 'current-server-nav-active' : ''}
          disabled={!guildId}
          onClick={() => guildId && select(routes.tasks(guildId))}
        >
          <ProjectOutlined /> Project Tasks
        </button>
        <button
          type="button"
          aria-label="Project Members"
          className={path.includes('/members') ? 'current-server-nav-active' : ''}
          disabled={!guildId}
          onClick={() => guildId && select(routes.members(guildId))}
        >
          <TeamOutlined /> Project Members
        </button>
        {activeGuild?.owner_id === currentUser?.id && (
          <button
            type="button"
            aria-label="Project Settings"
            className={path.match(/\/settings(?:\/|$)/) ? 'current-server-nav-active' : ''}
            onClick={() => guildId && select(routes.serverSettings(guildId))}
          >
            <SettingOutlined /> Project Settings
          </button>
        )}
      </nav>
      <Sidebar serverOnly onTaskClick={onTaskClick} activeThreadId={activeThreadId} activeChannelId={activeChannelId} />
    </aside>
  );
}
